import { useState, useEffect, useCallback } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { Button } from '@/components/common/Button'
import { ZoomIn, ZoomOut, Maximize2, Loader2, X, ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

interface PdfPageViewerProps {
  pdfBase64: string
  pageNumber: number
  totalPages: number
  open: boolean
  onClose: () => void
  subtitle?: string
  onPrev?: () => void
  onNext?: () => void
  prevDisabled?: boolean
  nextDisabled?: boolean
}

const SCALE_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 2]

export function PdfPageViewer({
  pdfBase64,
  pageNumber,
  open,
  onClose,
  subtitle,
  onPrev,
  onNext,
  prevDisabled,
  nextDisabled,
}: PdfPageViewerProps) {
  const { t } = useTranslation()
  const [scale, setScale] = useState(1)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setError(null)
  }, [pageNumber])

  const handleZoomIn = useCallback(() => {
    const currentIndex = SCALE_OPTIONS.indexOf(scale)
    const nextScale = SCALE_OPTIONS[currentIndex + 1]
    if (currentIndex < SCALE_OPTIONS.length - 1 && nextScale !== undefined) {
      setScale(nextScale)
    }
  }, [scale])

  const handleZoomOut = useCallback(() => {
    const currentIndex = SCALE_OPTIONS.indexOf(scale)
    const prevScale = SCALE_OPTIONS[currentIndex - 1]
    if (currentIndex > 0 && prevScale !== undefined) {
      setScale(prevScale)
    }
  }, [scale])

  const handleFitWidth = useCallback(() => {
    setScale(1.25)
  }, [])

  const handleResetZoom = useCallback(() => {
    setScale(1)
  }, [])

  const handleDocumentLoadSuccess = useCallback(() => {
    console.log('[PdfPageViewer] Document loaded successfully')
  }, [])

  const handleDocumentLoadError = useCallback((err: Error) => {
    console.error('[PdfPageViewer] Document load error:', err)
    setError(err.message || t('pdfViewer_loadError'))
  }, [t])

  const pdfDataUri = `data:application/pdf;base64,${pdfBase64}`

  if (!open) return null

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[80] p-4"
      onClick={onClose}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">{t('pdfViewer_title', 'PDF-Seite anzeigen')}</h2>
            {subtitle ? <p className="text-sm text-gray-600 truncate" title={subtitle}>{subtitle}</p> : null}
          </div>
          <button onClick={onClose} aria-label={t('pdfViewer_close', 'Schliessen')} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleZoomOut}
              disabled={scale === SCALE_OPTIONS[0]}
              title={t('pdfViewer_zoomOut', 'Verkleinern')}
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleZoomIn}
              disabled={scale === SCALE_OPTIONS[SCALE_OPTIONS.length - 1]}
              title={t('pdfViewer_zoomIn', 'Vergrössern')}
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleResetZoom}
              title={t('pdfViewer_resetZoom', '100%')}
            >
              {Math.round(scale * 100)}%
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleFitWidth}
              title={t('pdfViewer_fitWidth', 'An Breite anpassen')}
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex items-center gap-1">
            {onPrev ? (
              <Button variant="secondary" size="sm" onClick={onPrev} disabled={prevDisabled} title="Vorheriger Stundenplan">
                <ChevronLeft className="h-4 w-4" />
              </Button>
            ) : null}
            <span className="text-sm text-gray-600">
              {t('pdfViewer_singlePage', 'Seite {{current}}', { current: pageNumber })}
            </span>
            {onNext ? (
              <Button variant="secondary" size="sm" onClick={onNext} disabled={nextDisabled} title="Nächster Stundenplan">
                <ChevronRight className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-gray-100 p-4">
          <div className="flex items-center justify-center min-h-full">
            {error && (
              <div className="text-center text-red-500">
                <p>{error}</p>
                <div className="flex gap-2 mt-2 justify-center">
                  <Button variant="secondary" size="sm" onClick={onClose}>
                    {t('pdfViewer_close', 'Schliessen')}
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => setError(null)}>
                    {t('pdfViewer_retry', 'Erneut versuchen')}
                  </Button>
                </div>
              </div>
            )}

            {!error && (
              <Document
                file={pdfDataUri}
                onLoadSuccess={handleDocumentLoadSuccess}
                onLoadError={handleDocumentLoadError}
                loading={
                  <div className="flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                    <span className="ml-2 text-gray-500">{t('pdfViewer_loading', 'Laden...')}</span>
                  </div>
                }
              >
                <Page
                  pageNumber={1}
                  scale={scale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className="shadow-lg"
                />
              </Document>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
